import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
export function FAQs() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const infoPages = [{
    name: 'FAQs',
    path: '/helpful-info/faqs'
  }, {
    name: 'Compliance',
    path: '/helpful-info/compliance'
  }, {
    name: 'Screen Printing Advice',
    path: '/helpful-info/screen-printing-advice'
  }, {
    name: 'What is Vector Art?',
    path: '/helpful-info/vector-art'
  }, {
    name: 'The Wear Haus Way',
    path: '/helpful-info/wear-haus-way'
  }, {
    name: 'Terms and Conditions',
    path: '/helpful-info/terms-and-conditions'
  }, {
    name: 'Privacy Policy',
    path: '/helpful-info/privacy-policy'
  }];
  const faqs = [{
    question: 'Can you (insert crazy idea here)?',
    answer: "Probably! We love a challenge and are always up for trying new things. If you have a unique project in mind, reach out to us and let's see what we can create together."
  }, {
    question: 'Do I need to pay for my order now?',
    answer: 'We require a 50% deposit to begin production on your order. The remaining balance is due before we ship or when you pick up your order.'
  }, {
    question: 'Do you have a spoilage rate?',
    answer: 'Yes, we do have a spoilage rate. Due to the nature of custom printing and decoration, there is always a small chance that some items may not meet our quality standards. We typically recommend ordering 5-10% extra to account for potential spoilage.'
  }, {
    question: 'Can you work with items I purchased elsewhere?',
    answer: "Absolutely! We can decorate items you've purchased from other sources. Just bring them in or ship them to us, and we'll work our magic. Please note that we cannot guarantee the quality of items not purchased through us."
  }, {
    question: 'Can I mix shirt sizes & do I need to order in dozens?',
    answer: "Yes, you can mix sizes! You don't need to order in dozens - we can accommodate mixed size runs. This gives you the flexibility to order exactly what you need for your group or event."
  }, {
    question: 'Can I mix and match garment and decoration colors?',
    answer: 'Yes! You can mix garment colors and decoration colors within the same order. This allows you to create variety while still maintaining your design consistency.'
  }, {
    question: 'Is there a minimum order quantity?',
    answer: 'Minimum order quantities vary by decoration method. Screen printing typically has a 12-piece minimum, while Direct to Garment (DTG) has no minimum. Embroidery minimums depend on the complexity of the design. Contact us for specific details about your project.'
  }, {
    question: 'What is your turnaround time?',
    answer: 'Our standard turnaround time is 10-14 business days from the time we receive your approved artwork and deposit. Rush services are available for an additional fee if you need your order sooner.'
  }, {
    question: 'What are setup fees and why do you have them?',
    answer: "Setup fees cover the cost of preparing your artwork for production. This includes creating screens for screen printing, digitizing for embroidery, or preparing files for other decoration methods. These are one-time fees per design, and you won't be charged again for reorders of the same design."
  }];
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <div className="flex flex-col w-full" data-id="element-528">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-wh-teal to-teal-600 py-16" data-id="element-529">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-530">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-531">
            FAQs
          </h1>
          <p className="text-xl text-white/90" data-id="element-532">
            Here are the most frequently asked questions at Wear Haus Designs.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-50" data-id="element-533">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-534">
          <div className="flex flex-col lg:flex-row gap-8" data-id="element-535">
            {/* Sidebar Navigation */}
            <aside className="lg:w-64 flex-shrink-0" data-id="element-536">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-24" data-id="element-537">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-538">
                  Helpful Info
                </h3>
                <nav className="space-y-2" data-id="element-539">
                  {infoPages.map(page => <Link key={page.path} to={page.path} className={`block px-3 py-2 rounded-md text-sm font-medium transition-colors ${page.path === '/helpful-info/faqs' ? 'bg-wh-pink text-white' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-540">
                      {page.name}
                    </Link>)}
                </nav>
              </div>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1" data-id="element-541">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8" data-id="element-542">
                <div className="space-y-4" data-id="element-543">
                  {faqs.map((faq, index) => <div key={index} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-544">
                      <button onClick={() => toggleFAQ(index)} className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors" data-id="element-545">
                        <span className="text-lg font-bold text-gray-900 pr-8" data-id="element-546">
                          {faq.question}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-547" />
                      </button>
                      {openIndex === index && <div className="px-6 pb-6 pt-2 bg-gray-50" data-id="element-548">
                          <p className="text-gray-700 leading-relaxed" data-id="element-549">
                            {faq.answer}
                          </p>
                        </div>}
                    </div>)}
                </div>

                {/* Contact CTA */}
                <div className="mt-12 bg-gradient-to-br from-wh-pink to-pink-600 rounded-xl p-8 text-center text-white" data-id="element-550">
                  <h3 className="text-2xl font-bold mb-3" data-id="element-551">
                    Still have questions?
                  </h3>
                  <p className="text-lg text-white/90 mb-6" data-id="element-552">
                    We're here to help! Get in touch with us and we'll answer
                    any questions you have.
                  </p>
                  <Link to="/contact" className="inline-flex items-center justify-center px-8 py-3 bg-white text-wh-pink font-bold rounded-lg hover:bg-gray-100 transition-colors" data-id="element-553">
                    Contact Us
                  </Link>
                </div>
              </div>
            </main>
          </div>
        </div>
      </section>
    </div>;
}